import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { HardHat, ShieldCheck, Plus } from "lucide-react";

/**
 * v1.2 PR-C — Contractor Compliance (consolidated).
 *
 * Folds the previously-separate `/settings/ppe-requirements` and
 * `/settings/contractor-policy` pages into a single tab with anchored
 * sections (#ppe, #policy).
 *
 * Legacy URLs redirect via App.js.
 */

const SECTIONS = [
  { id: "ppe",    label: "PPE requirements",   icon: HardHat     },
  { id: "policy", label: "Sign-in policy",     icon: ShieldCheck },
];

function useScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 50);
    return () => window.clearTimeout(t);
  }, [hash]);
}

function SectionSubnav({ sections }) {
  const { hash } = useLocation();
  const nav = useNavigate();
  const active = (hash || `#${sections[0].id}`).replace(/^#/, "");
  return (
    <nav className="sticky top-16 z-10 flex flex-wrap gap-1.5 bg-surface/80 dark:bg-navy-900/80 backdrop-blur-sm py-2 -mx-1 px-1 rounded-lg"
         data-testid="section-subnav">
      {sections.map((s) => {
        const Icon = s.icon;
        const isActive = active === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => nav(`#${s.id}`, { replace: true })}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
              isActive
                ? "bg-orange-500 text-white border-orange-500"
                : "bg-white dark:bg-navy-800 text-navy-700 dark:text-navy-200 border-navy-200 dark:border-navy-700 hover:border-orange-300"
            }`}
            data-testid={`subnav-${s.id}`}
          >
            <Icon className="w-3.5 h-3.5" /> {s.label}
          </button>
        );
      })}
    </nav>
  );
}

export default function SettingsContractorCompliance() {
  useScrollToHash();
  return (
    <SettingsLayout>
      <div className="space-y-6" data-testid="settings-contractor-compliance-page">
        <div>
          <div className="eb-overline mb-1">Module 8 · Contractor compliance</div>
          <h1 className="text-3xl font-semibold text-navy-900 dark:text-white flex items-center gap-2">
            <HardHat className="w-7 h-7 text-orange-500" /> Contractor compliance
          </h1>
          <p className="text-sm text-navy-500 dark:text-navy-300 mt-1 max-w-2xl">
            Set per-site PPE requirements and org-wide policy gates for contractor sign-ins.
          </p>
        </div>
        <SectionSubnav sections={SECTIONS} />
        <section id="ppe" className="scroll-mt-24" data-testid="section-ppe">
          <PpeSection />
        </section>
        <div className="h-px bg-navy-100 dark:bg-navy-700" />
        <section id="policy" className="scroll-mt-24" data-testid="section-policy">
          <PolicySection />
        </section>
      </div>
    </SettingsLayout>
  );
}

// ============================================================================
// PPE section — verbatim port of SettingsPpeRequirements body.
// ============================================================================

const SUGGESTED = ["Hi-vis vest", "Hard hat", "Safety boots", "Eye protection", "Gloves", "Ear protection", "Respirator"];

function PpeSection() {
  const [sites, setSites] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/sites");
      setSites(data || []);
      const map = {};
      (data || []).forEach((s) => (map[s.id] = (s.ppe_requirements || []).slice()));
      setDraft(map);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (siteId, item) => {
    const cur = new Set(draft[siteId] || []);
    if (cur.has(item)) cur.delete(item); else cur.add(item);
    setDraft({ ...draft, [siteId]: Array.from(cur) });
  };

  const addCustom = (siteId) => {
    const v = window.prompt("Custom PPE item name:", "");
    if (!v) return;
    const cur = new Set(draft[siteId] || []);
    cur.add(v.trim());
    setDraft({ ...draft, [siteId]: Array.from(cur) });
  };

  const save = async (siteId) => {
    setBusy(siteId);
    try {
      await api.patch(`/sites/${siteId}`, { ppe_requirements: draft[siteId] || [] });
      toast.success("PPE requirements saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="ppe-settings-page">
      <div>
        <h2 className="text-xl font-semibold text-navy-900 dark:text-white flex items-center gap-2">
          <HardHat className="w-5 h-5 text-orange-500" /> PPE requirements (per site)
        </h2>
        <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">
          Items contractors must tick at sign-in. Empty list disables PPE check for that site.
        </p>
      </div>
      <div className="space-y-4">
        {sites.map((s) => {
          const items = new Set(draft[s.id] || []);
          return (
            <div key={s.id} className="rounded-2xl bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 p-5" data-testid={`ppe-card-${s.id}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-navy-900 dark:text-white">{s.name}</h3>
                <Button onClick={() => save(s.id)} disabled={busy === s.id} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid={`save-ppe-${s.id}`}>
                  {busy === s.id ? "Saving…" : "Save"}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {[...new Set([...SUGGESTED, ...(draft[s.id] || [])])].map((item) => (
                  <button
                    key={item}
                    onClick={() => toggle(s.id, item)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      items.has(item)
                        ? "bg-orange-500 text-white border-orange-500"
                        : "bg-white dark:bg-navy-800 text-navy-700 dark:text-navy-100 border-navy-200 dark:border-navy-700 hover:border-orange-500"
                    }`}
                    data-testid={`ppe-toggle-${s.id}-${item}`}
                  >
                    {item}
                  </button>
                ))}
                <button onClick={() => addCustom(s.id)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border border-dashed border-navy-300 dark:border-navy-600 text-navy-500 dark:text-navy-300 hover:text-orange-500 hover:border-orange-500 inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Custom
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Policy section — verbatim port of SettingsContractorPolicy body.
// ============================================================================

function PolicySection() {
  const [policy, setPolicy] = useState({
    require_dbs: false, require_cscs: false,
    require_permit_for_contractor_signin: false,
    induction_default_validity_days: 365,
  });
  const [extChannels, setExtChannels] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/orgs/me");
      setPolicy((p) => ({ ...p, ...(data.contractor_policy || {}) }));
      setExtChannels(!!data.watchlist_hits_to_external_channels);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/orgs/me", {
        contractor_policy: policy,
        watchlist_hits_to_external_channels: extChannels,
      });
      toast.success("Policy saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const Toggle = ({ k, label, description }) => (
    <label className="flex items-start gap-3 py-2 cursor-pointer" data-testid={`policy-toggle-${k}`}>
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-orange-500"
        checked={!!policy[k]}
        onChange={(e) => setPolicy({ ...policy, [k]: e.target.checked })}
      />
      <span>
        <span className="font-medium text-navy-900 dark:text-white">{label}</span>
        <span className="block text-xs text-navy-500 dark:text-navy-300">{description}</span>
      </span>
    </label>
  );

  return (
    <div className="space-y-4" data-testid="contractor-policy-page">
      <div>
        <h2 className="text-xl font-semibold text-navy-900 dark:text-white flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-orange-500" /> Sign-in policy
        </h2>
        <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">Toggle org-wide requirements for contractor sign-ins.</p>
      </div>
      <div className="rounded-2xl bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 p-5 max-w-2xl space-y-1">
        <Toggle k="require_dbs" label="Require DBS card"
          description="Contractor sign-in blocked if no in-date DBS card on file." />
        <Toggle k="require_cscs" label="Require CSCS card"
          description="Contractor sign-in blocked if no in-date CSCS card on file." />
        <Toggle k="require_permit_for_contractor_signin" label="Require active permit"
          description="Contractor sign-in blocked unless an active permit-to-work matches the site." />
        <div className="flex items-center gap-3 pt-3 border-t border-navy-100 dark:border-navy-700">
          <label className="text-sm text-navy-700 dark:text-navy-200 flex-1">
            Default induction validity (days)
            <input
              type="number" min={1} max={3650}
              className="w-32 ml-2 px-2 py-1 border border-navy-200 dark:border-navy-700 dark:bg-navy-700 dark:text-navy-100 rounded"
              value={policy.induction_default_validity_days || 365}
              onChange={(e) => setPolicy({ ...policy, induction_default_validity_days: Number(e.target.value) })}
              data-testid="policy-induction-days"
            />
          </label>
        </div>
        <div className="border-t border-navy-100 dark:border-navy-700 pt-3">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="policy-watchlist-ext-toggle">
            <input type="checkbox" className="mt-1 h-4 w-4 accent-orange-500"
              checked={extChannels}
              onChange={(e) => setExtChannels(e.target.checked)} />
            <span>
              <span className="font-medium text-navy-900 dark:text-white">Watchlist hits → Slack/Teams (opt-in)</span>
              <span className="block text-xs text-navy-500 dark:text-navy-300">
                Send a redacted "match logged" message (no PII) to your Slack/Teams integration.
                Default OFF — in-app only.
              </span>
            </span>
          </label>
        </div>
      </div>
      <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-policy-btn">
        {busy ? "Saving…" : "Save policy"}
      </Button>
    </div>
  );
}
